package org.gymly.navigation

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import org.gymly.canSwitchWebExperience

/**
 * Mantiene [state] sincronizado con el hash del navegador en web:
 * - Cambiar state → pushState en el historial
 * - Botón atrás/adelante → actualiza state
 */
@Composable
fun <T> rememberWebHistoryState(
    initial: T,
    hashFor: (T) -> String,
    parseFromHash: (String) -> T?,
): Pair<T, (T) -> Unit> {
    if (!canSwitchWebExperience()) {
        var state by remember { mutableStateOf(initial) }
        return state to { state = it }
    }

    var state by remember {
        mutableStateOf(parseFromHash(currentWebHash()) ?: initial)
    }
    var suppressPush by remember { mutableStateOf(false) }

    DisposableEffect(Unit) {
        val remove = installWebHashListener {
            suppressPush = true
            parseFromHash(currentWebHash())?.let { state = it }
        }
        onDispose { remove() }
    }

    LaunchedEffect(state) {
        if (suppressPush) {
            suppressPush = false
            return@LaunchedEffect
        }
        val target = hashFor(state)
        if (currentWebHash() != target) {
            pushWebHash(target)
        }
    }

    return state to { state = it }
}

/** Igual que [rememberWebHistoryState] pero la primera escritura usa replace (post-login). */
@Composable
fun <T> rememberWebHistoryStateWithInitialReplace(
    initial: T,
    hashFor: (T) -> String,
    parseFromHash: (String) -> T?,
): Pair<T, (T) -> Unit> {
    if (!canSwitchWebExperience()) {
        var state by remember { mutableStateOf(initial) }
        return state to { state = it }
    }

    var state by remember {
        mutableStateOf(parseFromHash(currentWebHash()) ?: initial)
    }
    var suppressPush by remember { mutableStateOf(false) }
    var didInitialReplace by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        if (currentWebHash() == hashFor(state)) {
            didInitialReplace = true
        }
    }

    DisposableEffect(Unit) {
        val remove = installWebHashListener {
            suppressPush = true
            parseFromHash(currentWebHash())?.let { state = it }
        }
        onDispose { remove() }
    }

    LaunchedEffect(state) {
        if (suppressPush) {
            suppressPush = false
            return@LaunchedEffect
        }
        val target = hashFor(state)
        if (currentWebHash() != target) {
            val replace = !didInitialReplace
            pushWebHash(target, replace = replace)
            didInitialReplace = true
        }
    }

    return state to { state = it }
}
